import { useState, useRef, useCallback, useMemo, useEffect, useContext, type FC } from 'react';
import { Typography } from '@mui/material';
import ButtonGlynt from '../common/Buttons/ButtonGlynt';
import MATDiscoveryTableHorizontal from './MATDiscoveryTableHorizontal';
import { btnSubtleNeutralSx } from '../common/filterStyles';
import MATDiscoveryTableHeader from './MATDiscoveryTableHeader';
import {
  coerce,
  normalizeKey,
  getExistingValueForCol,
  getFirstDiscoveredValueForCol,
  getOptions,
  copyFromRow,
  readDiscoveryPosition,
  nextPosition,
  previousPosition,
  positionAfterCreating,
  withDiscoveryPosition,
  type DiscoveryPosition,
} from './matDiscoveryTableHelpers';
import { AppContext } from '../../App';
import useMatDiscovery from './useMatDiscovery';
import useMatById from './useMatById';
import useGlynt from '../hooks/useGlynt';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';
import useSearchParams from '../hooks/useSearchParams';
import useMatFieldsInfo from './hooks/useMatFieldsInfo';
import { sanitizeMatRequestBody } from './MATRowDetailDrawer.utils';

const MATDiscoveryTable: FC = () => {
  const { org, setToast } = useContext(AppContext);
  const { search } = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(search);
  const { setSearchParam, removeSearchParamSingle } = useSearchParams();
  const position = readDiscoveryPosition(searchParams);
  const index = position.index;
  const discoveredOnly = (searchParams.get('has_existing_mat') || '') === 'false';
  const reviewedOnly = (searchParams.get('data_verified') || '') === 'true';

  const {
    data,
    isLoading: isDiscoveryLoading,
    isError,
  } = useMatDiscovery({
    has_existing_mat: discoveredOnly ? 'false' : undefined,
    data_verified: reviewedOnly ? 'true' : undefined,
    position,
  });
  const entries = data?.results ?? [];
  const total = data?.count ?? 0;
  const hasNext = data?.next != null;

  const current: any | undefined = entries[0];

  const { data: existingMat, isLoading: isMatLoading } = useMatById(
    org?.id,
    current?.mat_id || undefined
  );
  // Fields info (read-only metadata)
  const {
    data: fieldsInfo,
    isLoading: isFieldsInfoLoading,
    isError: isFieldsInfoError,
    refetch: refetchFieldsInfo,
  } = useMatFieldsInfo();

  const readOnlySet = useMemo(() => {
    if (!fieldsInfo) return new Set<string>();
    // Normalize all keys to canonical form using normalizeKey
    const allReadOnly = [
      ...(fieldsInfo.read_only_fields || []),
      ...(fieldsInfo.post_only_fields || []),
    ];
    return new Set<string>(allReadOnly.map((k) => normalizeKey(k)));
  }, [fieldsInfo, normalizeKey]);

  const requiredSet = useMemo(() => {
    if (!fieldsInfo) return new Set<string>();
    return new Set<string>([...(fieldsInfo.required_fields || [])]);
  }, [fieldsInfo]);

  const [requiredErrors, setRequiredErrors] = useState<Set<string>>(new Set());

  const blockEditing = isFieldsInfoLoading || isFieldsInfoError;

  const columns = useMemo(() => {
    const arr: string[] = [];
    // Discovered rows
    if (current?.discovered_rows) {
      current.discovered_rows.forEach((r: any) => {
        const d = r?.data || {};
        Object.keys(d).forEach((k) => {
          if (k.startsWith('__')) return;
          if (!arr.includes(k)) arr.push(k);
        });
      });
    }
    // Existing MAT record fields (ensure they are included even if not in any discovered row)
    if (existingMat && typeof existingMat === 'object') {
      Object.keys(existingMat).forEach((k) => {
        if (k.startsWith('__')) return;
        if (!arr.includes(k)) arr.push(k);
      });
    }
    return arr;
  }, [current, existingMat]);

  const [draft, setDraft] = useState<Record<string, any>>({});
  const prevIndexRef = useRef<number>(index);
  const prevMatIdRef = useRef<string | null | undefined>(null);
  const prevKeyRef = useRef<string | undefined>(current?.mat_key);

  useEffect(() => {
    const currentMatId = (existingMat as any)?.MatRowIndex ?? (existingMat as any)?.mat_id;
    const indexChanged = prevIndexRef.current !== index;
    const matIdChanged = prevMatIdRef.current !== currentMatId && currentMatId != null;
    const keyChanged = prevKeyRef.current !== current?.mat_key;
    const draftMissing = Object.keys(draft).length === 0 && columns.length > 0;
    const shouldInit = indexChanged || keyChanged || draftMissing || matIdChanged;
    if (!shouldInit) return;
    const next: Record<string, any> = {};
    columns.forEach((col) => {
      const fromExisting = getExistingValueForCol(col, existingMat);
      if (fromExisting != null) next[col] = coerce(fromExisting);
      else {
        const fromDiscovered = getFirstDiscoveredValueForCol(col, current);
        if (fromDiscovered != null) next[col] = coerce(fromDiscovered);
        else next[col] = '';
      }
    });
    setDraft(next);
    prevIndexRef.current = index;
    prevMatIdRef.current = currentMatId;
    prevKeyRef.current = current?.mat_key;
  }, [
    index,
    columns,
    current,
    existingMat,
    (existingMat as any)?.MatRowIndex,
    (existingMat as any)?.mat_id,
    draft,
  ]);

  const setField = useCallback(
    (key: string, value: any) => {
      if (readOnlySet.has(normalizeKey(key))) return;
      if (blockEditing) return;
      setDraft((prev) => ({ ...prev, [key]: value }));
    },
    [readOnlySet, blockEditing, normalizeKey]
  );

  const handleCopyFromRow = useCallback(
    (source: Record<string, any>) => {
      setDraft((prev) => {
        const writableFields: Record<string, any> = {};
        columns.forEach((col) => {
          // Normalize for read-only and identifier checks
          const normCol = normalizeKey(col);
          if (
            !readOnlySet.has(normCol) &&
            normCol !== normalizeKey('MatKey') &&
            normCol !== normalizeKey('mat_key')
          ) {
            if (source[col] !== undefined) {
              writableFields[col] = source[col];
            }
          }
        });
        return { ...prev, ...writableFields };
      });
    },
    [columns, readOnlySet, normalizeKey]
  );

  const handleGetOptions = (key: string): string[] => {
    return getOptions(key, existingMat, current);
  };

  const goTo = (target: DiscoveryPosition) => {
    navigate({ search: withDiscoveryPosition(searchParams, target).toString() }, { replace: true });
  };
  const onNext = () => current && goTo(nextPosition(position, current.mat_key));
  const onPrevious = () => goTo(previousPosition(position, current?.mat_key));

  // Submit handling (moved here from page): create MAT with current draft
  const glynt = useGlynt();
  const queryClient = useQueryClient();
  const createMut = useMutation({
    mutationFn: async (body: any) => {
      const g = await glynt;
      if (!org?.id) throw new Error('Missing orgId');
      return g.mat.create(org.id, body);
    },

    onSuccess: () => {
      // Invalidate mat discovery queries to refresh entries
      queryClient.invalidateQueries({
        queryKey: ['mat-discovery']
      });
      queryClient.invalidateQueries({
        queryKey: ['mat-discovery-keys']
      });
    }
  });

  const onSubmit = async () => {
    const matKey = draft['MatKey'] ?? draft['mat_key'];
    if (!matKey || String(matKey).trim() === '') {
      setToast({ open: true, variant: 'error', message: 'MatKey is required.' });
      return;
    }
    // Required field validation
    const missing: string[] = [];
    requiredSet.forEach((field) => {
      const v = draft[field];
      if (v == null || String(v).trim() === '') missing.push(field);
    });
    if (missing.length) {
      setRequiredErrors(new Set(missing));
      const msg = `${missing.join(', ')} ${missing.length === 1 ? 'is' : 'are'} required`;
      setToast({ open: true, variant: 'error', message: msg });
      return;
    } else if (requiredErrors.size) {
      setRequiredErrors(new Set());
    }
    try {
      await createMut.mutateAsync(sanitizeMatRequestBody(draft, { stripDataSourceStatus: true }));
      if (current) goTo(positionAfterCreating(position, current.mat_key, discoveredOnly));
      setToast({ open: true, variant: 'success', message: 'MAT entry created successfully.' });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Unknown error';
      setToast({
        open: true,
        variant: 'error',
        message: `Failed to create MAT entry: ${msg}`,
      });
    }
  };

  const loadingRow = isDiscoveryLoading || isMatLoading;

  return (
    <>
      <MATDiscoveryTableHeader
        current={current}
        discoveredOnly={discoveredOnly}
        reviewedOnly={reviewedOnly}
        isDiscoveryLoading={isDiscoveryLoading}
        index={index}
        total={total}
        hasNext={hasNext}
        blockEditing={blockEditing}
        onNext={onNext}
        onPrevious={onPrevious}
        searchParams={searchParams}
        navigate={navigate}
      />
      {isFieldsInfoLoading && (
        <Typography variant="body2" sx={{ mb: 1, opacity: 0.7 }}>
          Loading field editability…
        </Typography>
      )}
      {isFieldsInfoError && (
        <div style={{ marginBottom: 12 }}>
          <Typography color="error" variant="body2" component="span" style={{ marginRight: 8 }}>
            Failed to load field editability metadata.
          </Typography>
          <ButtonGlynt
            variant="outlined"
            sx={btnSubtleNeutralSx}
            onClick={() => refetchFieldsInfo()}
          >
            Retry
          </ButtonGlynt>
        </div>
      )}
      {isError ? (
        <Typography color="error">Failed to load discovery data.</Typography>
      ) : !current && !isDiscoveryLoading ? (
        <Typography>No discovery entries.</Typography>
      ) : (
        <>
          <MATDiscoveryTableHorizontal
            columns={columns}
            requiredSet={requiredSet}
            readOnlySet={readOnlySet}
            requiredErrors={requiredErrors}
            draft={draft}
            setField={setField}
            blockEditing={blockEditing}
            existingMat={existingMat}
            current={current}
            copyFromRow={handleCopyFromRow}
            loadingRow={loadingRow}
            coerce={coerce}
            getOptions={handleGetOptions}
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 0', width: '100%', maxWidth: '100%' }}>
            <ButtonGlynt
              variant="contained"
              color="primary"
              onClick={onSubmit}
              disabled={!org?.id || createMut.isPending || blockEditing}
            >
              {createMut.isPending ? 'Submitting…' : blockEditing ? 'Loading…' : 'Submit MAT Row'}
            </ButtonGlynt>
          </div>
        </>
      )}
    </>
  );
};

export default MATDiscoveryTable;
